import {
  Box,
  VStack,
  Text,
  Icon,
  Flex,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useBreakpointValue,
  Badge,
  Divider,
  Avatar,
  HStack,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
} from '@chakra-ui/react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { userAtom } from '../../state/userAtom';
import { isLicenseValidSelector, isLicenseExpiringSoonSelector } from '../../state/licenseAtom';
import { authApi } from '../../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faChartBar,
  faBullhorn,
  faImage,
  faLayerGroup,
  faFileAlt,
  faCog,
  faSignOutAlt,
  faGlobe,
  faLink,
  faChevronLeft,
  faChevronRight,
  faBuilding,
  faKey,
  faExclamationTriangle,
  faCheckCircle,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import FalconIcon from '../common/FalconIcon';
import ProfileModal from '../common/ProfileModal';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: faChartLine },
  { path: '/analytics', label: 'Web Stats', icon: faChartBar },
  { path: '/reports', label: 'Ad Reports', icon: faFileAlt },
];

const bottomNavItems = [
  { path: '/ads', label: 'Ads', icon: faImage },
  { path: '/campaigns', label: 'Campaigns', icon: faBullhorn },
  { path: '/advertisers', label: 'Advertisers', icon: faBuilding },
  { path: '/zones', label: 'Zones', icon: faLayerGroup },
  { path: '/links', label: 'Link Builder', icon: faLink },
  { path: '/sites', label: 'Sites', icon: faGlobe },
  { path: '/settings', label: 'Settings', icon: faCog },
];

function NavItem({ item, isCollapsed }) {
  const location = useLocation();
  const isActive = location.pathname === item.path;

  const activeBg = useColorModeValue(
    'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(217, 70, 239, 0.1) 100%)',
    'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(217, 70, 239, 0.15) 100%)'
  );
  const activeColor = useColorModeValue('brand.600', 'brand.300');
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const hoverTextColor = useColorModeValue('gray.900', 'white');
  const iconBgActive = useColorModeValue('brand.500', 'brand.400');

  const content = (
    <Box
      as={NavLink}
      to={item.path}
      w="100%"
      px={isCollapsed ? 2 : 3}
      py={2.5}
      borderRadius="xl"
      bg={isActive ? activeBg : 'transparent'}
      color={isActive ? activeColor : textColor}
      fontWeight={isActive ? '600' : '500'}
      position="relative"
      _hover={{
        bg: isActive ? activeBg : hoverBg,
        textDecoration: 'none',
        color: isActive ? activeColor : hoverTextColor,
        transform: isCollapsed ? 'scale(1.05)' : 'translateX(4px)',
      }}
      transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
      overflow="hidden"
      display="flex"
      justifyContent={isCollapsed ? 'center' : 'flex-start'}
    >
      {isActive && !isCollapsed && (
        <Box
          position="absolute"
          left={0}
          top="50%"
          transform="translateY(-50%)"
          w="3px"
          h="60%"
          bgGradient="linear(to-b, brand.400, accent.400)"
          borderRadius="full"
        />
      )}
      <Flex align="center" gap={3} justify={isCollapsed ? 'center' : 'flex-start'}>
        <Flex
          align="center"
          justify="center"
          w="32px"
          h="32px"
          borderRadius="lg"
          bg={isActive ? iconBgActive : 'transparent'}
          color={isActive ? 'white' : 'inherit'}
          transition="all 0.2s"
          flexShrink={0}
        >
          <FontAwesomeIcon icon={item.icon} style={{ width: '14px', height: '14px' }} />
        </Flex>
        {!isCollapsed && (
          <>
            <Text fontSize="sm" whiteSpace="nowrap">{item.label}</Text>
            {item.badge && (
              <Badge
                ml="auto"
                colorScheme="brand"
                variant="solid"
                borderRadius="full"
                px={2}
                py={0.5}
                fontSize="xs"
              >
                {item.badge}
              </Badge>
            )}
          </>
        )}
      </Flex>
    </Box>
  );

  if (isCollapsed) {
    return (
      <Tooltip label={item.label} placement="right" hasArrow>
        {content}
      </Tooltip>
    );
  }

  return content;
}

function SidebarContent({ onClose, isCollapsed, onToggleCollapse }) {
  const user = useRecoilValue(userAtom);
  const setUser = useSetRecoilState(userAtom);
  const navigate = useNavigate();
  const isLicenseValid = useRecoilValue(isLicenseValidSelector);
  const isExpiringSoon = useRecoilValue(isLicenseExpiringSoonSelector);
  const { isOpen: isProfileOpen, onOpen: onProfileOpen, onClose: onProfileClose } = useDisclosure();

  const bgColor = useColorModeValue(
    'rgba(255, 255, 255, 0.8)',
    'rgba(26, 32, 44, 0.8)'
  );
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const logoBg = useColorModeValue(
    'linear-gradient(135deg, #6366f1 0%, #d946ef 100%)',
    'linear-gradient(135deg, #818cf8 0%, #e879f9 100%)'
  );
  const userBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const toggleBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const toggleHoverBg = useColorModeValue('gray.200', 'whiteAlpha.200');

  const handleLogout = () => {
    authApi.logout();
    setUser(null);
    navigate('/login');
    if (onClose) onClose();
  };

  return (
    <Flex
      direction="column"
      h="100%"
      bg={bgColor}
      backdropFilter="blur(20px)"
      borderRight="1px"
      borderColor={borderColor}
      position="relative"
    >
      {/* Collapse Toggle Button - Desktop only */}
      {onToggleCollapse && (
        <IconButton
          icon={<FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />}
          size="sm"
          position="absolute"
          right="-12px"
          top="72px"
          zIndex={30}
          borderRadius="full"
          bg={toggleBg}
          _hover={{ bg: toggleHoverBg }}
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          boxShadow="md"
        />
      )}

      {/* Logo */}
      <Flex
        align="center"
        gap={3}
        px={isCollapsed ? 3 : 5}
        py={5}
        cursor="pointer"
        onClick={() => navigate('/dashboard')}
        _hover={{ opacity: 0.8 }}
        transition="opacity 0.2s"
        justify={isCollapsed ? 'center' : 'flex-start'}
      >
        <Flex
          align="center"
          justify="center"
          w="42px"
          h="42px"
          bgGradient={logoBg}
          borderRadius="xl"
          boxShadow="0 4px 14px 0 rgba(99, 102, 241, 0.4)"
          flexShrink={0}
        >
          <FalconIcon color="white" boxSize={6} />
        </Flex>
        {!isCollapsed && (
          <Box>
            <Text fontSize="2xl" fontWeight="700" fontFamily="logo" bgGradient={logoBg} bgClip="text" whiteSpace="nowrap" letterSpacing="-0.01em">
              StatFalcon
            </Text>
            <Text fontSize="xs" color={textSecondary} fontWeight="500" whiteSpace="nowrap">
              Web Analytics
            </Text>
          </Box>
        )}
      </Flex>

      <Divider borderColor={borderColor} />

      {/* Navigation */}
      <VStack spacing={1} align="stretch" px={isCollapsed ? 2 : 3} py={4} flex={1}>
        {navItems.map((item) => (
          <NavItem key={item.path} item={item} isCollapsed={isCollapsed} />
        ))}
      </VStack>

      {/* Bottom Section */}
      <VStack spacing={1} align="stretch" px={isCollapsed ? 2 : 3} pb={3}>
        <Divider borderColor={borderColor} mb={3} />
        {bottomNavItems.map((item) => (
          <NavItem key={item.path} item={item} isCollapsed={isCollapsed} />
        ))}

        {/* User Profile Section */}
        <Menu placement={isCollapsed ? 'right' : 'top'}>
          <MenuButton
            as={Box}
            mt={3}
            p={isCollapsed ? 2 : 3}
            bg={userBg}
            borderRadius="xl"
            cursor="pointer"
            _hover={{ opacity: 0.8 }}
            transition="opacity 0.2s"
          >
            {isCollapsed ? (
              <Flex justify="center">
                <Avatar
                  size="sm"
                  name={user?.name || 'User'}
                  bg="brand.500"
                  color="white"
                />
              </Flex>
            ) : (
              <HStack spacing={3}>
                <Avatar
                  size="sm"
                  name={user?.name || 'User'}
                  bg="brand.500"
                  color="white"
                />
                <Box flex={1} minW={0}>
                  <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                    {user?.name || 'User'}
                  </Text>
                  <Text fontSize="xs" color={textSecondary} noOfLines={1}>
                    {user?.email || ''}
                  </Text>
                </Box>
              </HStack>
            )}
          </MenuButton>
          <MenuList py={2} minW="200px" borderRadius="xl">
            <Box px={4} pb={2}>
              <Text fontWeight="600" fontSize="sm">{user?.name || 'User'}</Text>
              <Text fontSize="xs" color={textSecondary}>{user?.email || ''}</Text>
            </Box>
            <MenuDivider />
            <MenuItem
              icon={<FontAwesomeIcon icon={faUser} />}
              onClick={onProfileOpen}
              mx={2}
              borderRadius="lg"
              w="calc(100% - 16px)"
            >
              Profile
            </MenuItem>
            <MenuItem
              icon={<FontAwesomeIcon icon={faCog} />}
              onClick={() => { navigate('/settings'); if (onClose) onClose(); }}
              mx={2}
              borderRadius="lg"
              w="calc(100% - 16px)"
            >
              Settings
            </MenuItem>
            <MenuDivider />
            <MenuItem
              icon={<FontAwesomeIcon icon={faSignOutAlt} />}
              color="red.400"
              onClick={handleLogout}
              mx={2}
              borderRadius="lg"
              w="calc(100% - 16px)"
            >
              Sign Out
            </MenuItem>
          </MenuList>
        </Menu>

        {/* Profile Modal */}
        <ProfileModal isOpen={isProfileOpen} onClose={onProfileClose} />

        {/* License Status Indicator */}
        {isCollapsed ? (
          <Tooltip
            label={
              isLicenseValid
                ? isExpiringSoon
                  ? 'License expiring soon'
                  : 'License active'
                : 'License required'
            }
            placement="right"
            hasArrow
          >
            <Flex
              justify="center"
              p={2}
              mt={2}
              borderRadius="lg"
              bg={
                isLicenseValid
                  ? isExpiringSoon
                    ? 'yellow.100'
                    : 'green.100'
                  : 'red.100'
              }
              _dark={{
                bg: isLicenseValid
                  ? isExpiringSoon
                    ? 'yellow.900'
                    : 'green.900'
                  : 'red.900',
              }}
              cursor="pointer"
              onClick={() => navigate('/settings?tab=license')}
              _hover={{ opacity: 0.8 }}
            >
              <Icon
                color={
                  isLicenseValid
                    ? isExpiringSoon
                      ? 'yellow.500'
                      : 'green.500'
                    : 'red.500'
                }
                boxSize={4}
              >
                <FontAwesomeIcon
                  icon={
                    isLicenseValid
                      ? isExpiringSoon
                        ? faExclamationTriangle
                        : faCheckCircle
                      : faKey
                  }
                />
              </Icon>
            </Flex>
          </Tooltip>
        ) : (
          <Flex
            align="center"
            gap={2}
            p={2}
            mt={2}
            borderRadius="lg"
            bg={
              isLicenseValid
                ? isExpiringSoon
                  ? 'yellow.100'
                  : 'green.100'
                : 'red.100'
            }
            _dark={{
              bg: isLicenseValid
                ? isExpiringSoon
                  ? 'yellow.900'
                  : 'green.900'
                : 'red.900',
            }}
            cursor="pointer"
            onClick={() => navigate('/settings?tab=license')}
            _hover={{ opacity: 0.8 }}
          >
            <Icon
              color={
                isLicenseValid
                  ? isExpiringSoon
                    ? 'yellow.500'
                    : 'green.500'
                  : 'red.500'
              }
              boxSize={3}
            >
              <FontAwesomeIcon
                icon={
                  isLicenseValid
                    ? isExpiringSoon
                      ? faExclamationTriangle
                      : faCheckCircle
                    : faKey
                }
              />
            </Icon>
            <Text
              fontSize="xs"
              fontWeight="500"
              color={
                isLicenseValid
                  ? isExpiringSoon
                    ? 'yellow.700'
                    : 'green.700'
                  : 'red.700'
              }
              _dark={{
                color: isLicenseValid
                  ? isExpiringSoon
                    ? 'yellow.200'
                    : 'green.200'
                  : 'red.200',
              }}
            >
              {isLicenseValid
                ? isExpiringSoon
                  ? 'License expiring'
                  : 'License active'
                : 'License required'}
            </Text>
          </Flex>
        )}
      </VStack>
    </Flex>
  );
}

export const SIDEBAR_WIDTH = 260;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

function Sidebar({ isOpen, onClose, isCollapsed, onToggleCollapse }) {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const closeButtonBg = useColorModeValue('gray.100', 'whiteAlpha.100');

  // Desktop Sidebar
  if (!isMobile) {
    return (
      <Box
        as="nav"
        pos="fixed"
        left={0}
        top={0}
        h="100vh"
        w={isCollapsed ? `${SIDEBAR_COLLAPSED_WIDTH}px` : `${SIDEBAR_WIDTH}px`}
        zIndex={20}
        transition="width 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      >
        <SidebarContent isCollapsed={isCollapsed} onToggleCollapse={onToggleCollapse} />
      </Box>
    );
  }

  // Mobile Drawer (always expanded)
  return (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
      <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <DrawerContent maxW="280px">
        <DrawerCloseButton
          top={4}
          right={4}
          zIndex={10}
          borderRadius="full"
          bg={closeButtonBg}
        />
        <DrawerBody p={0}>
          <SidebarContent onClose={onClose} isCollapsed={false} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default Sidebar;
